// LoadErrorState — what a page (or a section of one) shows when its data
// could not be loaded because the SERVER failed (a lost connection never gets
// here: read requests wait for the connection and then complete).
//
// One calm, bilingual state everywhere instead of red raw text: an icon, a
// clear title, a human message (the server's own message when it is
// readable, otherwise a generic one — never "Failed to fetch" or "HTTP 500"),
// and a "Try again" button. Same visual language as the session screens:
// sidebar colour tokens, brand button, no shadows.
import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useSessionUi, SessionStyles, IconTile } from "./session/SessionUi";
import { getDisplayError } from "../utils/displayError";

const COPY = {
  en: { title: "Couldn't load this information", retry: "Try again" },
  ar: { title: "تعذّر تحميل هذه البيانات", retry: "حاول مرة أخرى" },
};

/**
 * @param {object} props
 * @param {unknown} [props.error]   Error / server body / string (sanitised).
 * @param {() => void} [props.onRetry]  Re-run the failed load. Without it the
 *   button reloads the page (the session is kept).
 * @param {"card" | "inline"} [props.variant]  "card" draws its own surface;
 *   "inline" sits inside an existing card.
 * @param {string} [props.title]  Override the title.
 */
export default function LoadErrorState({ error, onRetry, variant = "card", title, style }) {
  const ui = useSessionUi();
  const { palette, language } = ui;
  const copy = COPY[language] || COPY.en;
  const message = getDisplayError(error, language, { kind: "load" });
  const retry = onRetry || (() => window.location.reload());

  return (
    <div
      dir={ui.dir}
      role="alert"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 12,
        padding: "40px 24px",
        minHeight: 240,
        justifyContent: "center",
        ...(variant === "card"
          ? { background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 16 }
          : null),
        ...style,
      }}
    >
      <SessionStyles palette={palette} />
      <IconTile icon={AlertTriangle} tone="warning" palette={palette} />
      <div style={{ fontSize: 17, fontWeight: 800, color: palette.text, marginTop: 4 }}>{title || copy.title}</div>
      <p style={{ margin: 0, maxWidth: 440, fontSize: 14, lineHeight: 1.6, color: palette.textSecondary }}>{message}</p>
      <button
        type="button"
        className="nx-session-btn nx-session-btn--secondary nx-session-btn--compact"
        onClick={retry}
        style={{ marginTop: 6 }}
      >
        <RefreshCw size={14} aria-hidden="true" />
        {copy.retry}
      </button>
    </div>
  );
}
